/**
 * Attachment Service
 * Handles uploading, listing, downloading, previewing, restoring, and deleting 
 * file attachments for missions.
 */

import { getFreshToken } from '../utils/auth';
import { APP_CONFIG } from '../config/app';
import { getActiveWorkspaceId, workspaceApiHeaders } from '../utils/api/workspaceContext';

const API_URL = APP_CONFIG.apiUrl;

// Limits (must match Lambda)
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB per file
const MAX_STORAGE = 100 * 1024 * 1024 * 1024; // 100GB total

// Allowed MIME types
const ALLOWED_MIME_TYPES = [
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/json',
  // Images
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  // Archives
  'application/zip',
  'application/x-rar-compressed',
  'application/gzip',
  // Design files
  'application/x-figma',
  'application/sketch',
  // Video
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-m4v',
  // Audio
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
];

const MIME_FROM_EXT = {
  mov: 'video/quicktime',
  qt: 'video/quicktime',
  m4v: 'video/x-m4v',
  mp4: 'video/mp4',
  webm: 'video/webm',
};

export const resolveAttachmentMime = (file) => {
  const reported = file?.type || '';
  if (reported && ALLOWED_MIME_TYPES.includes(reported)) return reported;
  const ext = String(file?.name || '').split('.').pop()?.toLowerCase();
  const inferred = ext ? MIME_FROM_EXT[ext] : null;
  if (inferred && ALLOWED_MIME_TYPES.includes(inferred)) return inferred;
  return reported;
};

// File types that support preview
const PREVIEWABLE_TYPES = ['image', 'pdf', 'video', 'audio'];

/**
 * Auth + workspace room for API requests. Pin the workspace at the start of
 * a multi-step upload so a switch mid-flight cannot file it in the new room.
 */
async function getAuthHeaders(pinnedWorkspaceId) {
  const token = await getFreshToken();
  return {
    Authorization: `Bearer ${token}`,
    ...workspaceApiHeaders(pinnedWorkspaceId),
  };
}

/**
 * Validate a file before upload
 * @param {File} file - The file to validate
 * @returns {{valid: boolean, error?: string}}
 */
export function validateFile(file) {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }
  
  // Check file type (MIME, or extension — .mov often reports video/quicktime)
  if (!ALLOWED_MIME_TYPES.includes(resolveAttachmentMime(file))) {
    return { 
      valid: false, 
      error: 'File type not allowed. Supported: documents, images, videos, audio, archives.' 
    };
  }
  
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { 
      valid: false, 
      error: `File too large (${Math.round(file.size / (1024 * 1024))}MB). Maximum: ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB` 
    };
  }
  
  return { valid: true };
}

/**
 * Check if a file type supports preview
 * @param {string} fileType - The file type (image, pdf, video, etc.)
 * @returns {boolean}
 */
export function isPreviewable(fileType) {
  return PREVIEWABLE_TYPES.includes(fileType);
}

/**
 * Upload an attachment to a mission
 * @param {string} missionId - The mission ID to attach to
 * @param {File} file - The file to upload
 * @param {function} onProgress - Progress callback (0-100)
 * @param {string} note - Optional version note
 * @returns {Promise<{attachmentId: string, filename: string, ...}>}
 */
export async function uploadAttachment(missionId, file, onProgress = () => {}, note = null) {
  const workspaceId = getActiveWorkspaceId();
  const headers = await getAuthHeaders(workspaceId);
  
  // Validate file
  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }
  
  onProgress(5);
  
  // Step 1: Get presigned upload URL
  const urlResponse = await fetch(`${API_URL}/attachments/upload-url`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      missionId,
      fileName: file.name,
      mimeType: resolveAttachmentMime(file) || file.type,
      size: file.size,
      note: note || null,
    }),
  });
  
  if (!urlResponse.ok) {
    const error = await urlResponse.json();
    throw new Error(error.error || 'Failed to get upload URL');
  }
  
  const { uploadUrl, attachmentId } = await urlResponse.json();
  
  onProgress(15);
  
  // Step 2: Upload directly to R2
  // Use XMLHttpRequest for progress tracking
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        // Map upload progress to 15-85 range
        const uploadPercent = (e.loaded / e.total) * 70;
        onProgress(15 + uploadPercent);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error('Failed to upload file to storage'));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });
    
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
  
  onProgress(90);
  
  // Step 3: Confirm upload
  const confirmResponse = await fetch(`${API_URL}/attachments/confirm`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ attachmentId }),
  });
  
  if (!confirmResponse.ok) {
    const error = await confirmResponse.json();
    throw new Error(error.error || 'Failed to confirm upload');
  }
  
  onProgress(100);
  
  return await confirmResponse.json();
}

/**
 * List attachments for a mission
 * @param {string} missionId - The mission ID
 * @returns {Promise<{attachments: Array}>}
 */
export async function listAttachments(missionId) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/attachments/${missionId}`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list attachments');
  }
  
  return await response.json();
}

/**
 * Get a signed download URL for an attachment
 * @param {string} missionId - The mission ID
 * @param {string} attachmentId - The attachment ID
 * @returns {Promise<{downloadUrl: string, filename: string, expiresIn: number}>}
 */
export async function getDownloadUrl(missionId, attachmentId) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/attachments/${missionId}/${attachmentId}/download`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get download URL');
  }
  
  return await response.json();
}

/**
 * Get a signed preview URL for an attachment (inline viewing)
 * @param {string} missionId - The mission ID
 * @param {string} attachmentId - The attachment ID
 * @returns {Promise<{previewUrl: string, filename: string, mimeType: string, ...}>}
 */
export async function getPreviewUrl(missionId, attachmentId) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/attachments/${missionId}/${attachmentId}/preview`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get preview URL');
  }
  
  return await response.json();
}

/**
 * Download an attachment (opens in new tab or triggers download)
 * @param {string} missionId - The mission ID
 * @param {string} attachmentId - The attachment ID
 */
export async function downloadAttachment(missionId, attachmentId) {
  const { downloadUrl } = await getDownloadUrl(missionId, attachmentId);
  
  // Open in new tab - browser will handle download based on Content-Disposition
  window.open(downloadUrl, '_blank');
}

/**
 * Save an attachment through the OS folder picker when the browser supports
 * it (`showSaveFilePicker`). Falls back to a named blob download otherwise.
 */
export async function saveAttachmentAs(missionId, attachmentId, filename) {
  const suggestedName = filename || 'download';
  let fileHandle = null;
  if (typeof window.showSaveFilePicker === 'function') {
    try {
      fileHandle = await window.showSaveFilePicker({ suggestedName });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      fileHandle = null;
    }
  }

  const { downloadUrl } = await getDownloadUrl(missionId, attachmentId);
  let blob;
  try {
    const response = await fetch(downloadUrl);
    if (!response.ok) throw new Error('Failed to download file');
    blob = await response.blob();
  } catch {
    window.open(downloadUrl, '_blank');
    return;
  }

  if (fileHandle) {
    const writable = await fileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = suggestedName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
}

/**
 * Restore a version as the latest (creates a copy)
 * @param {string} missionId - The mission ID
 * @param {string} attachmentId - The attachment ID to restore
 * @param {string} note - Optional note for the restored version
 * @returns {Promise<{attachmentId: string, ...}>}
 */
export async function restoreVersion(missionId, attachmentId, note = null) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/attachments/${missionId}/${attachmentId}/restore`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ note }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to restore version');
  }
  
  return await response.json();
}

/**
 * Update the note for an attachment version
 * @param {string} missionId - The mission ID
 * @param {string} attachmentId - The attachment ID
 * @param {string} note - The new note (can be empty string to clear)
 * @returns {Promise<{attachmentId: string, note: string}>}
 */
export async function updateNote(missionId, attachmentId, note) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/attachments/${missionId}/${attachmentId}/note`, {
    method: 'PUT',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ note }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update note');
  }
  
  return await response.json();
}

/**
 * Delete an attachment
 * @param {string} missionId - The mission ID
 * @param {string} attachmentId - The attachment ID
 * @returns {Promise<void>}
 */
export async function deleteAttachment(missionId, attachmentId) {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/attachments/${missionId}/${attachmentId}`, {
    method: 'DELETE',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete attachment');
  }
}

/**
 * Get user's storage usage
 * @returns {Promise<{storageUsed: number, storageLimit: number, percentUsed: string}>}
 */
export async function getStorageUsage() {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/attachments/storage`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get storage usage');
  }
  
  return await response.json();
}

// Export limits for UI
export const ATTACHMENT_LIMITS = {
  maxFileSize: MAX_FILE_SIZE,
  maxStorage: MAX_STORAGE,
  allowedMimeTypes: ALLOWED_MIME_TYPES,
  previewableTypes: PREVIEWABLE_TYPES,
};
